import { ref, watch, onMounted, onUnmounted } from "vue";
import { useAuthStore } from "@/stores";

// Koristi se dok server ne posalje session_idle_timeout (u login/refresh odgovoru).
// Pravi period se podesava na backendu: SESSION_IDLE_TIMEOUT_MINUTES.
const DEFAULT_TIMEOUT_SECONDS = 15 * 60;
const MAX_WARNING_BEFORE_MS = 2 * 60 * 1000; // upozorenje max 2 minute pre isteka
const CHECK_INTERVAL_MS = 1000;
const ACTIVITY_WRITE_THROTTLE_MS = 1000;

// Deli se izmedju tabova: aktivnost u bilo kom tabu produzava sesiju u svim tabovima.
const LAST_ACTIVITY_KEY = "lastActivityAt";

const ACTIVITY_EVENTS = [
  "mousemove",
  "pointerdown",
  "keydown",
  "wheel",
  "scroll",
  "touchstart",
];
// capture: scroll ne bubble-uje, pa bez njega skrol unutar kontejnera (main, modali) ne bi bio uhvacen
const LISTENER_OPTIONS = { capture: true, passive: true };

function readLastActivity() {
  try {
    const value = Number(localStorage.getItem(LAST_ACTIVITY_KEY));
    return Number.isFinite(value) && value > 0 ? value : null;
  } catch {
    return null;
  }
}

function writeLastActivity(timestamp) {
  try {
    localStorage.setItem(LAST_ACTIVITY_KEY, String(timestamp));
  } catch {
    // bez localStorage-a radimo samo sa lokalnim tajmerom ovog taba
  }
}

// Dogadjaji unutar iframe-a (npr. TinyMCE editor) ne stizu do glavnog window-a,
// pa na iframe-ove sa istog origin-a kacimo iste listenere. Kljuc je document,
// jer iframe posle ucitavanja (srcdoc) dobija novi document.
const watchedFrames = new WeakSet();
const watchedFrameDocuments = new WeakSet();

function watchFrame(iframe, onActivity) {
  if (watchedFrames.has(iframe)) return;
  watchedFrames.add(iframe);

  const attach = () => {
    try {
      const frameDocument = iframe.contentDocument;
      if (!frameDocument || watchedFrameDocuments.has(frameDocument)) return;
      watchedFrameDocuments.add(frameDocument);
      ACTIVITY_EVENTS.forEach((event) =>
        frameDocument.addEventListener(event, onActivity, LISTENER_OPTIONS),
      );
    } catch {
      // cross-origin iframe - nemamo pristup njegovim dogadjajima
    }
  };
  iframe.addEventListener("load", attach);
  attach();
}

function watchFramesIn(node, onActivity) {
  if (node.nodeType !== Node.ELEMENT_NODE) return;
  if (node.tagName === "IFRAME") {
    watchFrame(node, onActivity);
    return;
  }
  node
    .querySelectorAll("iframe")
    .forEach((iframe) => watchFrame(iframe, onActivity));
}

export function useInactivityTimer() {
  const auth = useAuthStore();
  const showWarning = ref(false);
  const secondsLeft = ref(0);

  let checkInterval = null;
  let frameObserver = null;
  let lastActivity = Date.now();
  let lastWrite = 0;
  let loggingOut = false;

  function timeoutMs() {
    const seconds = Number(auth.sessionIdleTimeout);
    return (
      (Number.isFinite(seconds) && seconds > 0
        ? seconds
        : DEFAULT_TIMEOUT_SECONDS) * 1000
    );
  }

  function markActivity(force = false) {
    const now = Date.now();
    lastActivity = now;
    if (!force && now - lastWrite < ACTIVITY_WRITE_THROTTLE_MS) return;
    lastWrite = now;
    writeLastActivity(now);
  }

  function onActivity() {
    if (!auth.user) return;
    if (showWarning.value) return; // modal je otvoren, racuna se samo "Extend Session"
    markActivity();
  }

  // Racuna preostalo vreme iz poslednje aktivnosti umesto da broji setTimeout-ove,
  // pa je tacno i kad browser uspori tajmere u pozadinskom tabu ili se racunar uspava.
  function check() {
    if (!auth.user) {
      showWarning.value = false;
      return;
    }

    const last = Math.max(lastActivity, readLastActivity() ?? 0);
    const total = timeoutMs();
    const remaining = last + total - Date.now();

    if (remaining <= 0) {
      logoutNow();
      return;
    }

    showWarning.value = remaining <= Math.min(MAX_WARNING_BEFORE_MS, total / 2);
    secondsLeft.value = Math.ceil(remaining / 1000);
  }

  async function extendSession() {
    markActivity(true);
    showWarning.value = false;
    try {
      await auth.refreshAccessToken();
    } catch {
      // refreshAccessToken već handla redirect na login ako ne uspe
    }
  }

  async function logoutNow() {
    if (loggingOut) return;
    loggingOut = true;
    showWarning.value = false;
    try {
      await auth.logout();
    } finally {
      loggingOut = false;
    }
  }

  function onVisibilityChange() {
    if (document.visibilityState === "visible") check();
  }

  // Svez login: ne sme da nasledi lastActivityAt iz prethodne sesije
  watch(
    () => auth.user,
    (user, previousUser) => {
      if (user && !previousUser) markActivity(true);
    },
  );

  onMounted(() => {
    ACTIVITY_EVENTS.forEach((event) =>
      window.addEventListener(event, onActivity, LISTENER_OPTIONS),
    );
    document.addEventListener("visibilitychange", onVisibilityChange);
    watchFramesIn(document.body, onActivity);
    frameObserver = new MutationObserver((mutations) =>
      mutations.forEach((mutation) =>
        mutation.addedNodes.forEach((node) => watchFramesIn(node, onActivity)),
      ),
    );
    frameObserver.observe(document.body, { childList: true, subtree: true });
    // Posle reload-a vazi zajednicka vrednost iz localStorage-a (ako postoji)
    lastActivity = readLastActivity() ?? Date.now();
    checkInterval = setInterval(check, CHECK_INTERVAL_MS);
    check();
  });

  onUnmounted(() => {
    ACTIVITY_EVENTS.forEach((event) =>
      window.removeEventListener(event, onActivity, LISTENER_OPTIONS),
    );
    document.removeEventListener("visibilitychange", onVisibilityChange);
    frameObserver?.disconnect();
    clearInterval(checkInterval);
  });

  return { showWarning, secondsLeft, extendSession, logoutNow };
}
